import {
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  InterleavedBufferAttribute,
  Line,
  LineBasicMaterial,
  LineLoop,
  LineSegments,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  Points,
  PointsMaterial,
  Vector2,
  Vector3,
} from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { Text } from 'troika-three-text';
import _ from 'underscore';

import {
  CellGridVolumeModel,
  DynamicAssetType,
  FileVersionModel,
  PdfModel,
  PrismVolumeModel,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { getFileExtension } from '../../react/utils/path';
import { formattedDate } from '../../utils/dateUtils';
import { pairIterator } from '../../utils/generators';
import {
  getVersionNumber,
  sortFileVersionsByRevisionDateAndName,
} from '../../utils/models/file-version';
import { bufferAttributeUtils, getPositionArray } from '../../utils/three/buffer-attribute';
import { isWhite } from '../../utils/three/color';
import { disposeGeometriesAndMaterials } from '../../utils/three/dispose';
import { getMaterials } from '../../utils/three/material';
import { isDefined } from '../../utils/variables';
import { CoordinateManager } from '../coordinate-manager';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { raycastThroughBoundsTrees } from '../utilities/bounds-tree-raycast';
import { Point2d, toVector3 } from '../utilities/math-helpers';
import {
  applyFileVersionTransformation,
  FileVersionTransformation,
  getActiveFileVersionTransformation,
} from './components/dynamic-asset-file-version-transform';
import { heightLabelUtils } from './components/height-labels';
import { TextBatch } from './components/text-batch';
import { PointId } from './design/design-node/utils/point-node';
import {
  extractSkyMapPrimitiveExtras,
  extractSkyMapRootNodeAttributes,
} from './design/skymap-extras';
import { AssetLoadingStatus, AssetType, ElementObj } from './element-obj';
import { FileInfo } from './file-info/types';
import {
  getIfcObject,
  getLayerTree,
  getLayerVisibility,
  getNodeTree,
  hideHiddenLayers,
  IfcData,
  LayerTreeNode,
  setLayerVisibility,
} from './glb-layers';
import { HasVolumes } from './interfaces/has-cell-grid-volumes';
import { HasOpacity } from './interfaces/has-opacity';
import { LayerHighlight } from './layer-highlight';
import { ShapeEdges } from './shape-edges/shape-edges';
import { applySkyMapDrawing, isFlatDrawingPrimitive } from './styled-lines/skymap-drawing';
import { StyledLines } from './styled-lines/styled-lines';
import { CellGridVolume } from './volume/cell-grid-volume';
import { PrismVolume } from './volume/prism-volume';
import { VolumeHandler } from './volume/volume-handler';

export type GlbOptions = {
  name: string;
  fileId?: string;
  fileInfo: FileInfo;
  visible: boolean;
  MAS?: number;
  timelineContainerId?: string;
  wgs84?: {
    lng: string | number;
    lat: string | number;
  };
  pdfs?: PdfModel[];
  cellGridVolumes?: CellGridVolumeModel[];
  prismVolumes?: PrismVolumeModel[];
  skyMapVolumeSourceIds?: string[];
  dynamicAssetFileVersionTransforms?: FileVersionTransformation[];
  assetTagIds?: string[];
};

export class Glb extends ElementObj implements HasOpacity, HasVolumes {
  get assetType(): AssetType {
    return 'glb';
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(value: number) {
    this.setOpacity(value);
    this._opacity = value;
  }

  get glbData() {
    return this._glbData;
  }

  /**
   * The layers that can be hidden and shown, e.g. of a PDF or the storeys of an IFC model (see
   * getLayerTree), or the nodes of a glb file without such layers (see getNodeTree). Empty until
   * the data is loaded.
   */
  getLayerTree(): LayerTreeNode[] {
    if (!this._glbData) {
      return [];
    }

    if (this.layerTree?.data !== this._glbData) {
      const layers = getLayerTree(this._glbData);
      const tree =
        layers.length === 0 && this.nodeNames ? getNodeTree(this._glbData, this.nodeNames) : layers;
      this.layerTree = { data: this._glbData, tree };
    }

    return this.layerTree.tree;
  }

  /**
   * Keeps which layers are shown while the page is open, so that they are shown the same way when
   * the data is loaded again, e.g. after switching versions. Reloading the page shows them as in
   * the file again.
   */
  saveLayerVisibility() {
    this.layerVisibility = getLayerVisibility(this.getLayerTree());

    // Of the layers that are shown.
    if (this.isSelected()) {
      void this.shapeEdges?.show();
    }
  }

  /**
   * Shows and hides the layers as they were saved, see saveLayerVisibility.
   */
  private restoreLayerVisibility() {
    if (this.layerVisibility) {
      setLayerVisibility(this.getLayerTree(), this.layerVisibility);
    }
  }

  /**
   * Hides a layer, e.g. an object of an IFC model that is clicked in the scene, as in the layer
   * list, which is updated.
   */
  hideLayer(layer: Object3D) {
    layer.visible = false;
    this.saveLayerVisibility();
    publish(SubscriptionTopic.LayerVisibilityChanged, this);
  }

  /**
   * The layer that is inspected, from the scene or the layer list: it is highlighted, and the IFC
   * data of an object of an IFC model is shown in the layer list.
   */
  get inspectedLayer() {
    return this._inspectedLayer;
  }

  /**
   * Inspects the layer, see inspectedLayer, or stops inspecting without one.
   */
  inspectLayer(layer: Object3D | undefined) {
    this._inspectedLayer = layer;
    this.updateLayerHighlight();
    publish(SubscriptionTopic.LayerInspected, this);
  }

  /**
   * The layer that is hovered, in the layer list or in the scene, which is highlighted.
   */
  get hoveredLayer() {
    return this._hoveredLayer;
  }

  /**
   * Highlights the layer that is hovered in the layer list or in the scene, or none.
   */
  hoverLayer(layer: Object3D | undefined) {
    if (layer === this._hoveredLayer) {
      return;
    }

    this._hoveredLayer = layer;
    this.updateLayerHighlight();
    publish(SubscriptionTopic.LayerHovered, this);
  }

  private updateLayerHighlight() {
    this.layerHighlight.set({ hovered: this._hoveredLayer, inspected: this._inspectedLayer });
  }

  /**
   * Whether the data is an IFC model from the converter before ConvertIfcToGlb, which shows some
   * elements wrong. Not known before the data has loaded.
   */
  isFromLegacyIfcConverter() {
    return (
      this.getFileType() === 'Ifc' &&
      isDefined(this._glbData) &&
      !this.generator?.startsWith('ConvertIfcToGlb')
    );
  }

  /**
   * Gets the IFC data of the node of an object of an IFC model, with what its type holds.
   */
  getIfcObject(layer: Object3D) {
    return getIfcObject(layer, this.ifcTypes);
  }

  objType: DynamicAssetType = 'Glb';

  fileId?: string;
  versions: FileVersionModel[] | undefined;
  private curVersion?: FileVersionModel;
  selectedVersion?: FileVersionModel;
  private path?: string;

  cellGridVolumes: CellGridVolume[] = [];
  prismVolumes: PrismVolume[] = [];
  skyMapVolumeSourceIds: string[] = [];
  volumeHandler = new VolumeHandler(this);

  fileInfo?: FileInfo;
  uploadedBy: string | undefined;
  uploadDate: string | undefined;
  revision: string | undefined;
  revisionDate: string | undefined;
  description: string | undefined;
  fileVersionTransformations: FileVersionTransformation[] = [];
  pdfs: PdfModel[];

  private comment: string | undefined;
  private glbDataOffset: Vector3 = new Vector3(0, 0, 0);
  /** How far the scene's direct children were moved, see normalizeGlbTransformation. */
  private sceneOffset = new Vector3();
  /** Whether the data is the nodes of a DAE file, moved when loaded, see extractDaeGeometry. */
  private isDaeData = false;
  /** The index of each node of the data in the glb file's nodes. */
  private nodeIndices = new Map<Object3D, number>();

  private _glbData?: Object3D | Group;
  /** The layer tree of the data, see getLayerTree. */
  private layerTree?: { data: Object3D; tree: LayerTreeNode[] };
  /** The nodes of a glb file, with their names in the file, see getNodeTree. */
  private nodeNames?: Map<Object3D, string>;
  /** Which layers are shown, by their paths, see saveLayerVisibility. */
  private layerVisibility?: Record<string, boolean>;
  /** The program that wrote the data, asset.generator of the glb file. */
  private generator?: string;
  /** The IFC types of an IFC model, asset.extras.ifc.types, see getIfcObject. */
  private ifcTypes: IfcData[] = [];
  /** See hoveredLayer. */
  private _hoveredLayer?: Object3D;
  /** See inspectedLayer. */
  private _inspectedLayer?: Object3D;
  /** Highlights the hovered and inspected layers, but not the outlines of pages shown when selected. */
  private layerHighlight = new LayerHighlight(
    (object) =>
      object instanceof StyledLines ||
      object instanceof Text ||
      this.pageOutlines.has(object as LineLoop),
  );
  /** Whether loading waits for the conversion of the selected version, see loadModelData. */
  private waitingForConversion = false;
  private _opacity = 1;

  /** The borders of the pages of a PDF, shown when the asset is selected. */
  private pageOutlines = new Set<LineLoop>();
  /** The edges of the shapes of the meshes of the data, shown when the asset is selected. */
  private shapeEdges?: ShapeEdges;

  constructor(
    public assetId: string | undefined,
    private sharedViewId: string | undefined,
    options: GlbOptions,
  ) {
    super(options.timelineContainerId!);

    this.sharedViewId = sharedViewId;
    this.cellGridVolumes = CellGridVolume.fromPropertyArray(options.cellGridVolumes);
    this.prismVolumes = PrismVolume.fromPropertyArray(options.prismVolumes);
    this.skyMapVolumeSourceIds = options.skyMapVolumeSourceIds ?? [];
    this.volumeHandler.activeVolume = this.cellGridVolumes[0] ?? this.prismVolumes[0];

    options = options || {};
    this.fileId = options.fileId;
    this.pdfs = options.pdfs ?? [];
    this.assetId = assetId;
    this.selectedVersion = undefined;
    this.MAS = options.MAS ?? 0;
    this.fileVersionTransformations = options.dynamicAssetFileVersionTransforms ?? [];

    this.wgs84 = this.initPosition(options.wgs84);

    this.extractDocumentInformation(options.fileInfo);
    void this.setStateVisibility(options.visible === false ? false : true);

    this.freeze = {
      translate: !this.canBeTransformed(),
      rotateZ: !this.canBeTransformed(),
      rotateXY: this.getFileType() === 'Image',
      scale: true,
    };

    this._assetTagIds = options.assetTagIds ?? [];
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'idle';
  }

  canBeScaled() {
    return this.getFileType() === 'Image';
  }

  canBeTransformed() {
    const type = this.getFileType();
    switch (type) {
      case 'Dae':
      case 'Glb':
      case 'Ifc':
      case 'Image':
        return true;
      default:
        return false;
    }
  }

  initPosition(wgs84?: { lng: number | string; lat: number | string }): {
    lng: number;
    lat: number;
  } {
    if (wgs84) {
      return { lng: Number(wgs84.lng), lat: Number(wgs84.lat) };
    }

    const wgs84Coords = this.calculateWGS84Coords(new Vector2(0, 0));
    return { lng: wgs84Coords?.x ?? 0, lat: wgs84Coords?.y ?? 0 };
  }

  setOpacity(value: number) {
    // Made again from the materials as they are changed.
    this.layerHighlight.dispose();

    this.traverse((node) => {
      const materials = (node as Mesh).material;
      for (const material of Array.isArray(materials) ? materials : [materials]) {
        if (material instanceof Material) {
          // Flat drawings are drawn in their draw order, without depth writes, see applySkyMapDrawing.
          const flatDrawing = material.userData.flatDrawing === true;
          material.opacity = value;
          material.depthWrite = value === 1 && !flatDrawing;
          material.transparent = value < 1 || flatDrawing;
          material.depthTest = true;
          material.needsUpdate = true;
          material.premultipliedAlpha = value !== 1;
          material.alphaTest = 0;
        }
      }
    });

    this.updateLayerHighlight();
  }

  async onStateVisibilityChanged() {
    if (this.visible && this.loadingStatus !== 'loading') {
      await this.loadModelData();
    }
    await super.onStateVisibilityChanged();
  }

  getFileType() {
    const extName = getFileExtension(this.fileInfo?.name.toLocaleLowerCase() ?? 'file.glb');
    switch (extName) {
      case '.glb':
        return 'Glb';
      case '.pdf':
      case '.png':
      case '.jpg':
      case '.jpeg':
        return 'Image';
      case '.geo':
        return 'Geo';
      case '.ifc':
        return 'Ifc';
      // Converted by ConvertDwgToGlb, see its FORMAT.md in skymap-oda.
      case '.dwg':
      case '.dxf':
        return 'Dwg';
      case '.kml':
      case '.kmz':
        return 'Kml';
      case '.shp':
      case '.shape':
        return 'Shape';
      case '.json':
      case '.geojson':
        return 'GeoJSON';
      case '.zip':
      case '.dae':
        return 'Dae';
      default:
        return 'LandXml';
    }
  }

  async loadModelData() {
    // otherwise dispose loaded data and load new.
    if (isDefined(this._glbData) || this.loadingStatus === 'loading') {
      return;
    }

    // A drawing whose version is being converted is loaded when the conversion has finished, see
    // onChange.
    if (this.isConverting()) {
      this.waitingForConversion = true;
      publish(SubscriptionTopic.AssetChanged, this);
      return;
    }

    try {
      this.loadingStatus = 'loading';
      this._glbData = await this.downloadGlbData();
      this.restoreLayerVisibility();
      this.add(this._glbData);
      if (this.isSelected()) {
        void this.shapeEdges?.show();
      }
      this.loadingStatus = 'loaded';
    } catch (err) {
      this.visible = false;
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
    }
  }

  /**
   * Whether the file is a drawing that ConvertDwgToGlb converts: a DWG or DXF, or a ZIP that can
   * hold one. All of its versions are converted when it is added to SkyView, see AssetManager.
   */
  isDrawing() {
    const extension = getFileExtension(this.fileInfo?.name.toLowerCase() ?? '');
    return ['.dwg', '.dxf', '.zip'].includes(extension);
  }

  /**
   * Whether the selected version of a drawing is being converted to a glb, so it can't be shown
   * until it has finished.
   */
  isConverting(): boolean {
    const version = this.fileInfo?.versions.find((x) => x.id === this.selectedVersion?.id);
    return this.isDrawing() && version?.glb?.status === 'RUNNING';
  }

  /**
   * Normalizes the transformation of the GLB scene.
   * We set the offset of the data and move all the direct children of the scene close to the origin.
   * If we detect the file to be Y-up we also rotate it so the data will be correctly positioned in the viewer.
   */
  private normalizeGlbTransformation(data: GLTF) {
    this.sceneOffset.set(0, 0, 0);

    // File is already normalized. Nothing to do.
    if (data.scene.userData && data.scene.userData.normalized === true) {
      console.log('File is already normalized, skipping');
      return;
    }

    if (data.scene.children.length > 0) {
      const localPosition = data.scene.children[0].position.clone();

      // Arbitrary treshold for when we safely can consider the file to be Y-up.
      const yUpTreshold = 10000;

      // If the Z value is very large it indicates that the file was Y-up in which case we want to rotate the scene 90-degrees by the X-axis.
      if (Math.abs(localPosition.z) > yUpTreshold) {
        data.scene.rotateX(Math.PI / 2);
      }

      // Update matrix world after rotating the object otherwise localToWorld will not return the correct position.
      data.scene.updateMatrixWorld(true);

      // Set the offset to the new worldPosition and update all the children.
      this.glbDataOffset = data.scene.localToWorld(localPosition.clone());
      this.sceneOffset.copy(localPosition);
      data.scene.children.forEach((child) => child.position.sub(localPosition));
    }
  }

  async downloadGlbData() {
    const data = await this.loadSkyMapInformationModel(this.selectedVersion?.id, this.sharedViewId);
    this.nodeNames = this.getFileType() === 'Glb' ? getNodeNames(data) : undefined;
    this.generator = data.asset.generator;
    this.ifcTypes =
      (data.asset.extras as { ifc?: { types?: IfcData[] } } | undefined)?.ifc?.types ?? [];
    this.nodeIndices = getNodeIndices(data);

    this.normalizeGlbTransformation(data);
    this.calculateLocalPosition();

    const extras = extractSkyMapRootNodeAttributes(data.scene);
    const textAttributes = [...extras.attributes.values()].filter(
      (x) => x.type === 'string' && x.display,
    );

    const isLandXml = this.getFileType() === 'LandXml';
    // A ZIP is a DAE, or it can hold a drawing that ConvertDwgToGlb converts.
    const isDae =
      this.getFileType() === 'Dae' && !data.asset.generator?.startsWith('ConvertDwgToGlb');
    const isGlb = this.getFileType() === 'Glb';
    // Images, and PDFs from before ConvertPdfToGlb, are a textured quad, shown unlit. PDFs from
    // ConvertPdfToGlb are drawings with colored primitives, shown like any other glb.
    const isRasterImage =
      this.getFileType() === 'Image' && !data.asset.generator?.startsWith('ConvertPdfToGlb');

    const textMaterial = new MeshBasicMaterial({
      transparent: true,
      opacity: 1,
      side: DoubleSide,
    });

    // The meshes whose shapes are outlined when the asset is selected, see ShapeEdges.
    const edgeMeshes: Mesh[] = [];
    data.scene.traverse((node) => {
      const material = (node as Mesh).material as MeshBasicMaterial | undefined;
      const overwriteColor =
        isLandXml &&
        isDefined(material) &&
        isWhite(material.color) &&
        material.vertexColors !== true;

      if (node instanceof Mesh) {
        if (isRasterImage) {
          const material = new MeshBasicMaterial({
            map: node.material.map,
            color: new Color(0xffffff),
            side: DoubleSide,
          });
          node.material = material;
        } else {
          const materials = getMaterials(node);
          for (const material of materials) {
            if (material instanceof MeshStandardMaterial) {
              // Metalness does not work correctly without a scene environment.
              material.metalness = 0.0;
              material.roughness = 1.0;
              if (overwriteColor) {
                material.color = new Color().setHSL(0.1, 0.56, 0.55);
              }
              if (!(isDae || isGlb)) {
                material.side = DoubleSide;
              }

              // Push surface polygons back in depth so that coplanar points
              // and lines render on top of the surfaces instead of z-fighting.
              material.polygonOffset = true;
              material.polygonOffsetFactor = 1;
              material.polygonOffsetUnits = 1;
              material.needsUpdate = true;
            }
          }

          // Texts are placed by their shader, so their edges would be of a unit square, and the
          // primitives of flat drawings are replaced with StyledLines.
          if (node.visible && !(node instanceof Text) && !isFlatDrawingPrimitive(node)) {
            edgeMeshes.push(node);
          }
        }
      }

      if (node instanceof Points && node.material instanceof PointsMaterial) {
        if (overwriteColor) {
          node.material.color = new Color(0, 0, 1);
        }

        node.material.transparent = false;
        node.material.opacity = 1.0;
        node.material.size = 8.0;
        node.material.sizeAttenuation = false;

        if (
          textAttributes.length > 0 &&
          node.geometry instanceof BufferGeometry &&
          node.geometry.index
        ) {
          const position = node.geometry.getAttribute('position') as
            | Float32BufferAttribute
            | InterleavedBufferAttribute;
          const colors = node.geometry.getAttribute('color') as
            | Float32BufferAttribute
            | InterleavedBufferAttribute;
          const primitiveExtras = extractSkyMapPrimitiveExtras<PointId>(node.geometry);
          // A drawing can have thousands of texts, which are drawn together.
          const texts = new TextBatch();
          texts.material = textMaterial;

          const newIndices = [];
          for (const index of node.geometry.index.array as unknown as PointId[]) {
            const pointAttributes = primitiveExtras.get(index);

            if (!isDefined(pointAttributes)) {
              newIndices.push(index);
              continue;
            }

            let hasText = false;
            for (const attribute of textAttributes) {
              const textAttribute = pointAttributes.get(attribute.id);
              if (isDefined(textAttribute) && textAttribute.type === 'string') {
                hasText = true;
                const vertex = bufferAttributeUtils.getVector3(index, position);
                const color = bufferAttributeUtils.getVector3(index, colors);
                const text = new Text();
                texts.add(text);
                text.text = textAttribute.value;

                // Increase the size with a few % to match the font sizing displayed in TrueView.
                const sizeFactor = 1.33;
                text.fontSize = (textAttribute.height ?? 0.5) * sizeFactor;
                text.color = new Color(color.x, color.y, color.z).getHex();
                text.anchorY = 'bottom-baseline';
                text.setRotationFromAxisAngle(new Vector3(0, 0, 1), textAttribute.rotation ?? 0);
                text.position.copy(vertex);
                void text.sync();
              }
            }

            if (!hasText) {
              newIndices.push(index);
            }
          }
          node.geometry.setIndex(newIndices);

          if (texts.texts.length > 0) {
            node.add(texts);
          }
        }
      }

      if (node instanceof LineSegments && node.material instanceof LineBasicMaterial) {
        node.material.transparent = false;
      }
    });

    hideHiddenLayers(data.scene);
    applySkyMapDrawing(data.scene);

    // The objects of an IFC model are picked as the mouse moves over them, see SelectTool.
    if (this.getFileType() === 'Ifc') {
      raycastThroughBoundsTrees(data.scene);
    }

    // The page of a PDF, see ConvertPdfToGlb's FORMAT.md in skymap-oda.
    data.scene.traverse((node) => {
      const page = node.userData.pdf as { size?: [number, number] } | undefined;

      if (page?.size) {
        this.addPageOutline(node, page.size[0], page.size[1]);
      }
    });

    this.shapeEdges = new ShapeEdges(this, edgeMeshes);
    this.isDaeData = isDae;
    if (isDae) {
      return extractDaeGeometry(data);
    }

    return data.scene;
  }

  /** Whether the data is a DAE file's, whose nodes are moved out of their parents when loaded. */
  isDae() {
    return this.isDaeData;
  }

  /**
   * Gets the matrix from the glb file's scene to local coordinates, as the data is shown, for the
   * backend to draw it in the CAD drawing report. Undefined until the data is loaded, and for DAE
   * files, whose nodes are moved out of their parents when loaded.
   */
  getSceneToLocalMatrix(): Matrix4 | undefined {
    if (!this._glbData || this.isDaeData) {
      return undefined;
    }

    this._glbData.updateWorldMatrix(true, false);
    // The scene's direct children were moved by the offset, which the file doesn't know about.
    const offset = new Matrix4().makeTranslation(this.sceneOffset.clone().negate());
    return this._glbData.matrixWorld.clone().multiply(offset);
  }

  /**
   * Gets the indices in the glb file's nodes of the nodes that aren't shown, by a layer tree or by
   * the file's own off layers, for the backend to leave them out of the CAD drawing report. A
   * node's descendants are left out with it, so they aren't listed.
   */
  getHiddenNodes(): number[] {
    const hidden: number[] = [];
    const visit = (object: Object3D, parentHidden: boolean) => {
      const isHidden = parentHidden || !object.visible;
      const index = this.nodeIndices.get(object);
      if (isHidden && index !== undefined) {
        hidden.push(index);
        return;
      }

      object.children.forEach((child) => visit(child, isHidden));
    };

    if (this._glbData) {
      visit(this._glbData, false);
    }

    return hidden;
  }

  /**
   * Adds the border of a PDF page, which is shown instead of edges when selected. The page
   * runs from (0, 0) to its size in the coordinates of its node.
   */
  addPageOutline(page: Object3D, width: number, height: number) {
    const outline = new LineLoop(
      new BufferGeometry().setFromPoints([
        new Vector3(0, 0, 0),
        new Vector3(width, 0, 0),
        new Vector3(width, height, 0),
        new Vector3(0, height, 0),
      ]),
      new LineBasicMaterial({ color: new Color(0, 1, 0) }),
    );

    outline.visible = false;
    // Only a highlight, it can't be picked or snapped to, also when hidden.
    outline.raycast = () => undefined;
    this.pageOutlines.add(outline);
    page.add(outline);
  }

  onSelect() {
    super.onSelect();
    for (const outline of this.pageOutlines) {
      outline.visible = true;
    }

    void this.shapeEdges?.show();
  }

  refreshLabelsGroup() {
    if (!this.labelsGroup) {
      return;
    }

    if (this.labelsSettings?.showHeightLabels) {
      this.traverse((child) => {
        if (child instanceof Points && child.geometry instanceof BufferGeometry) {
          for (const point of getPositionArray(child)) {
            heightLabelUtils.createLabel(
              this.labelsGroup!,
              point,
              this.labelsSettings?.color,
              this.labelsSettings?.fontSize,
              this.labelsSettings?.sizeAttenuation ?? true,
              this.labelsSettings?.decimalPlaces,
            );
          }
        }
      });
    }

    if (this.labelsSettings?.showLengthLabels) {
      this.traverse((child) => {
        if (
          (child instanceof LineSegments || child instanceof Line) &&
          !this.shapeEdges?.owns(child)
        ) {
          const positions = bufferAttributeUtils.getArray(child) ?? [];
          for (const [a, b] of pairIterator(positions)) {
            heightLabelUtils.createLengthLabel(
              this.labelsGroup!,
              a,
              b,
              this.labelsSettings?.color,
              this.labelsSettings?.fontSize,
              this.labelsSettings?.sizeAttenuation ?? true,
              this.labelsSettings?.decimalPlaces,
            );
          }
        }
      });
    }
  }

  onDeselect() {
    super.onDeselect();
    for (const outline of this.pageOutlines) {
      outline.visible = false;
    }

    this.shapeEdges?.hide();
  }

  extractDocumentInformation(fileInfo: FileInfo) {
    if (!fileInfo) {
      return;
    }

    this.fileInfo = fileInfo;
    this.versions = sortFileVersionsByRevisionDateAndName(fileInfo.versions ?? []);
    this.path = fileInfo.path;
    this.curVersion = undefined;

    fileInfo.versions.map(
      (version: FileVersionModel & { labelId?: string; uploadedAt?: string }) => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        version.labelId = version.revision ? `Rev ${version.revision} - ` : '';
        version.labelId += formattedDate(version.uploadedAt ?? version.createdAt);
      },
    );

    this.selectedVersion = this.versions ? this.versions[0] : undefined;
    this.setVersion(this.selectedVersion!);
  }

  /**
   * @param version
   * @description Set the version information for this LandXml
   */
  setVersion(version: FileVersionModel) {
    this.name = [this.fileInfo!.name, getVersionNumber(this.versions!, version.id)].join(
      ' - Version #',
    );

    const firstName: string = version.createdBy?.firstName ?? '';
    const lastName: string = version.createdBy?.lastName ?? '';
    this.uploadedBy = `${firstName} ${lastName}`;

    this.uploadDate = version.createdAt;
    this.revision = version.revision;
    this.revisionDate = version.revisionDate;
    this.description = version.description;

    this.curVersion = version;
  }

  async onChange() {
    if (this.selectedVersion && this.curVersion !== this.selectedVersion) {
      this.setVersion(this.selectedVersion);
      this.waitingForConversion = false;

      if (this.visible) {
        this.clearData();
        await this.loadModelData();
      }
    }

    // The conversion of the drawing has finished: the file info is updated with the asset, when
    // the backend sends DynamicAssetUpdated.
    if (this.waitingForConversion && !this.isConverting()) {
      this.waitingForConversion = false;

      if (this.visible) {
        this.clearData();
        await this.loadModelData();
      }
    }

    if (this.autoSave) {
      await this.save();
    }

    publish(SubscriptionTopic.AssetChanged, this);
  }

  clearData() {
    // The layers of the data are gone.
    this._hoveredLayer = undefined;
    this.layerHighlight.dispose();
    if (this._inspectedLayer) {
      this.inspectLayer(undefined);
    }

    disposeGeometriesAndMaterials(this.glbData);

    for (const child of this.children) {
      this.remove(child);
    }

    this._glbData = undefined;
    this.pageOutlines = new Set();
    this.shapeEdges?.hide();
    this.shapeEdges = undefined;
  }

  getSaveData() {
    const saveData: any = {
      type: this.objType,
      properties: {
        name: this.name,
        visible: this.getStateVisibility(),
        MAS: this.MAS ?? 0,
      },
      fileId: this.fileId ?? undefined,
      timelineContainerId: this.timelineContainerId,
    };

    if (this.assetId) {
      saveData.id = this.assetId;
    }

    const wgs84 = this.calculateWGS84Coords(new Vector2(this.position.x, this.position.y));
    if (wgs84) {
      saveData.properties.wgs84 = {
        lng: wgs84.x.toString(),
        lat: wgs84.y.toString(),
      };
    }

    return Promise.resolve(saveData);
  }

  onSaved(data: any) {
    if (!data) {
      return;
    }

    this.assetId = data.id || this.assetId;
    this.timelineContainerId = data.timelineContainerId;
  }

  getNativeAssetData() {
    return {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
    };
  }

  calculateLocalPosition(): Point2d | undefined {
    if (
      isDefined(
        this.fileVersionTransformations.find((x) => x.fileVersionId === this.selectedVersion?.id),
      )
    ) {
      /**
       * If specific transformation exists. position it based on the transformation of the file version.
       */
      const localPosition = toVector3(super.calculateLocalPosition() ?? new Vector2(0, 0));
      localPosition.setZ(this.calculateLocalHeight() ?? 0);

      const { position, quaternion, scale } = getActiveFileVersionTransformation(
        this,
        localPosition,
      );

      applyFileVersionTransformation(this, quaternion, scale, position);
    } else {
      /**
       * Position as georeferenced data.
       * i.e. the glbDataOffset + the local position is the geographical position.
       */
      const projectionOrigo =
        CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3(0, 0, 0);
      const newPosition = this.glbDataOffset.clone().sub(projectionOrigo);
      this.position.set(newPosition.x, newPosition.y, newPosition.z);
      this.rotation.set(0, 0, 0);
    }

    return;
  }

  equals(glb: Glb) {
    const sameId = () => this.assetId === glb.assetId;
    const sameUpdateTime = () => this.fileInfo?.updatedAt === glb.fileInfo?.updatedAt;
    const sameVolumeSourceIds = () =>
      _.isEqual(this.skyMapVolumeSourceIds.sort(), glb.skyMapVolumeSourceIds.sort());
    const sameVersions = () =>
      _.isEqual(this.versions?.map((x) => x.id).sort(), glb.versions?.map((x) => x.id).sort());

    return sameId() && sameUpdateTime() && sameVolumeSourceIds() && sameVersions();
  }

  canBeDisplayed(): boolean {
    if (!this.selectedVersion) {
      return true;
    }

    const pdf = this.pdfs.find((x) => x.fileVersionId === this.selectedVersion?.id);

    if (pdf) {
      return pdf.status === 'COMPLETED';
    }

    return !this.isConverting();
  }
}

/**
 * Gets the nodes of a glb file with their names in the file, which the loader replaces spaces and
 * some other characters in. The meshes that the loader makes for the primitives of a node are not
 * nodes.
 */
function getNodeNames(data: GLTF): Map<Object3D, string> {
  const nodes = (data.parser.json as { nodes?: { name?: string }[] }).nodes ?? [];
  const names = new Map<Object3D, string>();

  data.parser.associations.forEach((reference, object) => {
    if (object instanceof Object3D && isDefined(reference?.nodes)) {
      names.set(object, nodes[reference.nodes]?.name ?? object.name);
    }
  });

  return names;
}

/**
 * Gets the index of each node in the glb file's nodes, from the loader.
 */
function getNodeIndices(data: GLTF) {
  const indices = new Map<Object3D, number>();
  for (const [object, association] of data.parser.associations) {
    if (object instanceof Object3D && association.nodes !== undefined) {
      indices.set(object, association.nodes);
    }
  }

  return indices;
}

/**
 * For DAE files, return the nested content instead of the whole scene.
 * (This is the same behaviour as we had when it was a static asset)
 *
 * Note:
 * Originally we only added the first child of the first child as such:
 * data.scene.children[0].children[0]
 *
 * Now we iterate and add all of the children since we sometimes experienced that
 * some geometry was missing for special files.
 *
 * For now we skip direct children that are of type Mesh, this is because
 * in some files like "byggbod.dae" we have encountered meshes placed as
 * direct children to the scene that are not supposed to be rendered.
 * There are no other clear parameters indicating that is shouldn't be rendered.
 *
 * We have no known cases where this causes problems but it is possible
 * that it's not a universal rule.
 */
function extractDaeGeometry(data: GLTF) {
  const dae = new Object3D();
  const notAMesh = (x: Object3D) => !(x instanceof Mesh);
  for (const child of data.scene.children.filter(notAMesh).flatMap((x) => x.children)) {
    dae.add(child);
  }
  return dae;
}
